import './loanDocumentSuccessPage.scss';

import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { uniqueId } from 'lodash';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import logo from '../../../assets/logo-default.svg';
import heroSuccess from '../../../assets/success-hero.svg';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { resetStore } from '../../../store/_core/resetStore';
import useNewLoanDocumentStore from '../../../store/loanDocumentStore/useNewLoanDocumentStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useUserStore from '../../../store/userStore/useUserStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { setDrawerOpen } = useNavigationPaneStore();
  const { handleSubmit } = useForm();

  const {
    computed: { userFirstName },
  } = useUserStore();

  useEffect(() => {
    // Reset store when unmounting
    return (): void => {
      resetStore(useNewLoanDocumentStore);
    };
  }, []);

  const formSubmit = (): void => {
    // replace, as we do not want the user to come back to this page
    navigate('/dashboard/garage', { replace: true });
  };

  return (
    <div className="loan-document-success-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <header data-testid={locator.pages.loanDocumentSuccess.header.root}>
            <img alt={t('imageAlts.logo')} src={logo} />
            <button
              aria-label="Toggle drawer"
              data-testid={
                locator.pages.loanDocumentSuccess.header.drawerButton
              }
              type="button"
              onClick={() => {
                setDrawerOpen(true);
              }}
            >
              <MenuRoundedIcon />
            </button>
          </header>

          <hr />

          <main>
            <figure>
              <img alt={t('imageAlts.heroSuccess')} src={heroSuccess} />
            </figure>
            <form
              data-testid={locator.pages.loanDocumentSuccess.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.loanDocumentSuccess.form.h1}>
                  <Trans
                    components={[<br key={uniqueId('br-')} />]}
                    i18nKey={'LoanDocumentSuccessPage.title'}
                    values={{
                      firstName: userFirstName,
                    }}
                  />
                </h1>
                <h2 data-testid={locator.pages.loanDocumentSuccess.form.h2}>
                  {t('LoanDocumentSuccessPage.subtitle')}
                </h2>

                <div style={{ marginBottom: '3rem' }}>
                  <LoadingButton
                    color="primary"
                    data-testid={
                      locator.pages.loanDocumentSuccess.form.submitButton
                    }
                    startIcon={<></>}
                    type="submit"
                    variant="contained"
                    fullWidth
                  >
                    {t('LoanDocumentSuccessPage.submit')}
                  </LoadingButton>
                </div>
              </div>
              <footer
                data-testid={locator.pages.loanDocumentSuccess.footer.root}
              >
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'LoanDocumentSuccessPage';
